import type { Project, ThreadShell } from "../../types";

export interface ArchivedThreadGroup {
  /** Null for orphan threads whose projectId doesn't match any known project. */
  readonly project: Project | null;
  readonly threads: ThreadShell[];
}

/**
 * Groups archived threads by project. Archived threads whose projectId does
 * not match any known project are collected into a single fallback group with
 * `project: null` so they are never silently dropped (important for web users
 * who may have threads from the primary environment without local projects).
 */
export function groupArchivedThreads(
  projects: Project[],
  threads: ThreadShell[],
): ArchivedThreadGroup[] {
  const archivedThreads = threads.filter((t) => t.archivedAt !== null);
  if (archivedThreads.length === 0) return [];

  const projectById = new Map(projects.map((p) => [p.id, p]));

  const byProject = new Map<string | null, ThreadShell[]>();

  for (const thread of archivedThreads) {
    const project = projectById.get(thread.projectId);
    const key = project ? thread.projectId : null;
    const existing = byProject.get(key);
    if (existing) {
      existing.push(thread);
    } else {
      byProject.set(key, [thread]);
    }
  }

  const groups: ArchivedThreadGroup[] = [];

  // Emit project groups first (ordered by project insertion)
  for (const project of projects) {
    const projectThreads = byProject.get(project.id);
    if (projectThreads && projectThreads.length > 0) {
      groups.push({ project, threads: projectThreads });
    }
  }

  // Emit orphan group last (null project key)
  const orphans = byProject.get(null);
  if (orphans && orphans.length > 0) {
    groups.push({ project: null, threads: orphans });
  }

  return groups;
}

/**
 * Structural subset of `CmmdTask` this panel needs. Kept local so the grouping
 * stays unit-testable without pulling the full CMMD contract in.
 */
export interface ArchivedCmmdTaskInput {
  readonly id: string;
  readonly title: string;
  readonly projectId: string | null;
  readonly status: string | null;
  readonly archivedAt?: string | null;
}

export interface ArchivedCmmdProjectInput {
  readonly id: string;
  readonly title: string;
}

export interface ArchivedCmmdTaskGroup {
  /** Null when the task carries no project at all. */
  readonly projectId: string | null;
  /** Section heading: the project's title, its raw id, or the no-project label. */
  readonly title: string;
  readonly tasks: readonly ArchivedCmmdTaskInput[];
}

/**
 * Groups archived CMMD tasks by their CMMD project, newest archive first.
 *
 * Mirrors `groupArchivedThreads`: a task whose `projectId` does not match a
 * known CMMD project still gets a group (headed by the raw id) rather than
 * being dropped. The work-intake snapshot only carries the projects the user
 * can see, so an unknown id is expected — not a reason to hide the task.
 */
export function groupArchivedCmmdTasks(
  projects: readonly ArchivedCmmdProjectInput[],
  tasks: readonly ArchivedCmmdTaskInput[],
): ArchivedCmmdTaskGroup[] {
  const archived = tasks
    .filter((task) => task.archivedAt != null)
    .toSorted((left, right) => (right.archivedAt ?? "").localeCompare(left.archivedAt ?? ""));
  if (archived.length === 0) return [];

  const titleByProjectId = new Map(projects.map((project) => [project.id, project.title]));
  const byProject = new Map<string | null, ArchivedCmmdTaskInput[]>();
  for (const task of archived) {
    const existing = byProject.get(task.projectId);
    if (existing) {
      existing.push(task);
    } else {
      byProject.set(task.projectId, [task]);
    }
  }

  return [...byProject.entries()].map(([projectId, groupTasks]) => ({
    projectId,
    title: projectId ? (titleByProjectId.get(projectId) ?? projectId) : "Archived tasks",
    tasks: groupTasks,
  }));
}
